import React, { useRef, useState } from 'react';

import { Link } from '@material-ui/core';
// only for presentational purpose
import DetailIconComponent from 'components/MeshList/DetailIconComponent';
import { StyledAvatar } from 'components/avatars';
import { useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import CheckInTime from 'screens/common/sidepanel/CheckIn/CheckInTime';
import { getDateTooltip } from 'screens/common/sidepanel/DetailSection/GoalDetailSection';
import styled, { css } from 'styled-components';
import { StyledTypography, StyledGrid } from 'styles/common';

import { APP_SOURCE_CONTAINERS, ONE_ON_ONE_STATUSES } from 'constants/ProjectConstants';
import { getOneOnOneInstancePageRoute, getProfileRoute } from 'constants/helper';

import { showErrorSnackbar } from 'services/snackbar';
import { updateCheckInDateTime } from 'services/task';

import DateComp from 'uiToolkit/DateComp';

const StyledDetailIconComponent = styled(DetailIconComponent)`
  position: absolute;
  top: 20px;
  right: 20px;
`;

const Wrapper = styled.div`
  display: flex;
  padding: 24px;
  position: relative;
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
  box-sizing: border-box;
  cursor: pointer;
  &:hover {
    box-shadow:
      0 1px 2px 0 ${(props) => props.theme.palette.shadows.card},
      0 2px 6px 2px ${(props) => props.theme.palette.shadows.card};
    background-color: ${(props) => props.theme.palette.background.blue.light.v2};
  }

  &:hover ${StyledDetailIconComponent} {
    visibility: visible;
    border-radius: 50%;
    background-color: white;
  }
`;

const DetailsWrapper = styled.div`
  display: flex;
  margin-left: 24px;
  flex-direction: column;
  flex-grow: 1;
`;

const BorderAvatar = styled(StyledAvatar)`
  ${({ showBorder, theme }) =>
    showBorder &&
    css`
      border: 3px solid ${theme.palette.green.main};
      background-color: ${theme.palette.green.main};
    `}
  box-sizing: border-box;
`;

const DateTimeWrapper = styled(StyledGrid)`
  display: flex;
  justify-content: space-between;
`;

const getOtherPerson = (employees, employeeId) => employees.filter((employee) => employee.id !== employeeId);

const CheckInListCard = ({
  task,
  employee,
  onClickHandler,
  history,
  style,
  sourceContainer = APP_SOURCE_CONTAINERS.WEB_APP,
}) => {
  const [coloredStartDate, setColoredStartDate] = useState(task.coloredStartDate);
  const [coloredDueDate, setColoredDueDate] = useState(task.coloredDueDate);
  const isPlugin = sourceContainer === APP_SOURCE_CONTAINERS.PLUGIN;

  const dispatch = useDispatch();

  const dateTimeEditable = [ONE_ON_ONE_STATUSES.NOT_STARTED, ONE_ON_ONE_STATUSES.ACTIVE].includes(task.checkInState);

  // as both participants of CheckIn could be managers
  const isHigherManager = task.from.id === employee.id;
  const otherMember = getOtherPerson(task.toIds, employee.id)[0];
  const avatar = otherMember?.getProfileImage();
  const otherMemberFullName = otherMember?.fullName;
  const arrowIconRef = useRef();

  const goToProfilePage = (id) => history.push(getProfileRoute(id));

  const dateTimeChangeHandler = async (newStartDateTime, newDueDateTime) => {
    if (!newStartDateTime || !newDueDateTime) {
      dispatch(showErrorSnackbar('Please select a date for 1:1'));
      return;
    }

    try {
      const res = await updateCheckInDateTime({
        checkInInstanceId: task.id,
        dueDate: newDueDateTime.getTime(),
        startDate: newStartDateTime.getTime(),
      });

      setColoredStartDate(res.coloredStartDate);
      setColoredDueDate(res.coloredDueDate);
    } catch (err) {
      dispatch(showErrorSnackbar(err.message || 'Request Failed: Please check your connection'));
      console.error(err);
    }
  };

  const preventAnchorPropagationClickHandler = (e) => {
    if (arrowIconRef.current && arrowIconRef.current.contains(e.target)) {
      return;
    }
    onClickHandler(e);
  };

  const webAppLinkHref = `${window.location.origin}${getOneOnOneInstancePageRoute(task.id)}`;

  return (
    <Wrapper onClick={preventAnchorPropagationClickHandler} style={style}>
      <BorderAvatar
        showBorder={!isHigherManager}
        size={isPlugin ? 'fixed48px' : 'smallMedium'}
        src={avatar}
        onClick={(e) => {
          e.stopPropagation();
          goToProfilePage(otherMember.id);
        }}
      />
      <DetailsWrapper>
        <StyledTypography variant="body1" customColor="#0B4870" topGutter={0.25} bottomGutter={1}>
          {otherMemberFullName}
        </StyledTypography>

        <DateTimeWrapper container flex alignItems="center">
          <StyledGrid item xs={7}>
            <CheckInTime
              startDateTime={coloredStartDate.timeStamp ? new Date(coloredStartDate.timeStamp) : null}
              endDateTime={coloredDueDate.timeStamp ? new Date(coloredDueDate.timeStamp) : null}
              onDateTimeChange={dateTimeChangeHandler}
              editable={dateTimeEditable}
              isCompleted={coloredDueDate.isCrossed}
            />
          </StyledGrid>
          <StyledGrid item xs={5}>
            <DateComp
              startDateTime={coloredStartDate.timeStamp ? new Date(coloredStartDate.timeStamp) : null}
              endDateTime={coloredDueDate.timeStamp ? new Date(coloredDueDate.timeStamp) : null}
              color={coloredDueDate.color}
              tooltip={getDateTooltip({
                date: coloredDueDate.timeStamp,
                reason: coloredDueDate.reason,
                status: task.detailedStatus,
              })}
              isCrossed={coloredDueDate.isCrossed}
              checkInInstanceId={task.id}
              onDateTimeChange={dateTimeChangeHandler}
              editable={dateTimeEditable}
            />
          </StyledGrid>
        </DateTimeWrapper>
      </DetailsWrapper>
      {isPlugin ? (
        <Link ref={arrowIconRef} href={webAppLinkHref} target="_blank">
          <StyledDetailIconComponent tooltipText="View on Mesh.ai" />
        </Link>
      ) : (
        <StyledDetailIconComponent />
      )}
    </Wrapper>
  );
};

export default withRouter(CheckInListCard);
